import { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { Layout } from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import AddContent from "./pages/AddContent";
import KnowledgeBase from "./pages/KnowledgeBase";
import ContentDetail from "./pages/ContentDetail";
import Contents from "./pages/Contents";
import Contesto from "./pages/Contesto";
import { useButtonRipples } from "./hooks/useButtonRipples";
import LiveHome from "./pages/LiveHome";
import LiveSession from "./pages/LiveSession";
import Coach from "./pages/Coach";
import CallAnalytics from "./pages/CallAnalytics";
import ScriptManual from "./pages/ScriptManual";
import Users from "./pages/Users";
import MyPailot from "./pages/MyPailot";
import Chiamate from "./pages/Chiamate";
import SalesHelp from "./pages/SalesHelp";
import FollowUps from "./pages/FollowUps";
import Impostazioni from "./pages/Impostazioni";

function Shell() {
  const { user, loading } = useAuth();
  const [mode, setMode] = useState("login");
  useButtonRipples();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 spin-slow text-primary" />
      </div>
    );
  }

  if (!user) {
    return mode === "register" ? (
      <Register onSwitch={() => setMode("login")} />
    ) : (
      <Login onSwitch={() => setMode("register")} />
    );
  }

  const f = user.features || {};
  const sales = f.sales_help !== false;
  const anyModule = sales || f.mypailot !== false || f.chiamate !== false;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={anyModule ? <Dashboard /> : <Navigate to="/nessun-modulo" replace />} />
        <Route path="/followup" element={<FollowUps />} />
        <Route path="/impostazioni" element={<Impostazioni />} />
        {sales && (
          <>
            <Route path="/help" element={<SalesHelp />} />
            <Route path="/aggiungi" element={<AddContent />} />
            <Route path="/conoscenza" element={<KnowledgeBase />} />
            <Route path="/contenuti" element={<Contents />} />
            <Route path="/contenuti/:id" element={<ContentDetail />} />
            <Route path="/contesto" element={<Contesto />} />
            <Route path="/live" element={<LiveHome />} />
            <Route path="/live/panoramica" element={<CallAnalytics />} />
            <Route path="/live/:id" element={<LiveSession />} />
            <Route path="/coach" element={<Coach />} />
            <Route path="/manuale" element={<ScriptManual />} />
          </>
        )}
        {f.mypailot !== false && <Route path="/mypailot" element={<MyPailot />} />}
        {f.chiamate !== false && <Route path="/chiamate" element={<Chiamate />} />}
        {user.role === "admin" && <Route path="/utenti" element={<Users />} />}
        <Route path="/nessun-modulo" element={<NoModules />} />
        <Route path="*" element={<Navigate to={anyModule ? "/" : "/nessun-modulo"} replace />} />
      </Route>
    </Routes>
  );
}

const NoModules = () => (
  <div data-testid="no-modules" className="surface mx-auto max-w-lg p-10 text-center">
    <h1 className="text-xl font-semibold">Nessuna funzionalità attiva</h1>
    <p className="mt-2 text-sm text-muted-foreground">L'amministratore non ha ancora abilitato moduli per il tuo account. Contattalo per attivare Sales Help, MyPailot o Chiamate.</p>
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" richColors />
    </div>
  );
}

export default App;
